import decodeJwt from "jwt-decode";

import {
  UPLOADED_RESTAURANT_COVER_IMAGES_KEY,
  UPLOADED_RESTAURANT_PRODUCT_IMAGES_KEY,
  UPLOADED_VIDEOS_THUMBNAIL_KEY,
} from "../dataProvider";
import {
  forceLogout,
  validateAuthorization,
  validateResponse,
} from "../utils/AuthUtils";

const { REACT_APP_BASE_URL } = process.env;

/*
 * TECH DEBT: this module talks to the API with raw fetch instead of going through
 * the dataProvider, which react-admin expects to be the single data layer (so
 * calls get caching, auth and consistent error handling for free).
 *
 * It was left as-is on purpose. This is a parallel data layer with its own
 * auth-error policy — validateAuthorization / validateResponse / forceLogout from
 * utils/AuthUtils — which differs from the dataProvider's checkAuthError.
 * Migrating means reconciling two logout behaviours on 401/403 inside the
 * onboarding and login flows, where a mistake is expensive. It deserves its own
 * task, not a drive-by change.
 *
 * When picking it up:
 *  - fetchRestaurant maps to getOne; the four fetch* list helpers map to getList;
 *    deleteProduct maps to delete.
 *  - checkRegion below is a duplicate of dataProvider.checkRegion — delete it and
 *    use that one.
 *  - updateRestaurant, createOrUpdateProduct, completeOnboarding, changePassword
 *    and isUserConfirmed need custom dataProvider methods (the provider already
 *    has ~20 of those, so the pattern exists).
 *  - transformToFormData duplicates the FormData handling the dataProvider
 *    already does on create/update.
 */

const UPLOADED_IMAGES_KEY = "uploadedImages";

const addFilterActiveTrueAndSort = (url, sort) =>
  `${url}?filter=${JSON.stringify({
    active: true,
  })}${sort ? `&sort=${JSON.stringify(sort)}` : ""}`;

export const ApiService = {
  fetchRestaurant: async () => {
    try {
      const token = localStorage.getItem("token");
      const decodedToken = decodeJwt(token);
      const restaurantId = decodedToken.restaurant;
      const res = await fetch(
        `${REACT_APP_BASE_URL}/restaurants/${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      validateResponse(res);
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error al obtener el restaurante", error);
      return null;
    }
  },

  fetchProducts: async () => {
    try {
      const token = localStorage.getItem("token");
      const decodedToken = decodeJwt(token);
      const restaurantId = decodedToken.restaurant;
      const res = await fetch(
        `${REACT_APP_BASE_URL}/products?restaurant=${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // if restaurant is not found it will throw a 422 error
      if (res.status === 422) {
        forceLogout();
      }

      validateAuthorization(res);

      const data = await res.json();

      return data.map((e) => ({
        ...e,
        ...(e.customMorfySplitPercentage && {
          customMorfySplitPercentage:
            Number(e.customMorfySplitPercentage) * 100,
        }),
      }));
    } catch (error) {
      console.error("Error al obtener los productos", error);
      return [];
    }
  },

  fetchPlaceTypes: async () => {
    try {
      const token = localStorage.getItem("token");
      const sort = ["name", "ASC"];
      const url = addFilterActiveTrueAndSort(
        `${REACT_APP_BASE_URL}/place-types`,
        sort
      );
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      validateResponse(res);
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error al obtener los place types", error);
      return [];
    }
  },

  fetchPlaceTargets: async () => {
    try {
      const token = localStorage.getItem("token");
      const sort = ["name", "ASC"];
      const url = addFilterActiveTrueAndSort(
        `${REACT_APP_BASE_URL}/place-targets`,
        sort
      );
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      validateResponse(res);

      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error al obtener los place targets", error);
      return [];
    }
  },

  fetchCategories: async () => {
    try {
      const token = localStorage.getItem("token");
      const sort = ["name", "ASC"];
      const url = addFilterActiveTrueAndSort(
        `${REACT_APP_BASE_URL}/categories`,
        sort
      );
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      validateResponse(res);
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error al obtener las categorías", error);
      return [];
    }
  },

  fetchDataToPopulateForm: async () => {
    const [placeTypes, placeTargets, categories, products] = await Promise.all([
      ApiService.fetchPlaceTypes(),
      ApiService.fetchPlaceTargets(),
      ApiService.fetchCategories(),
      ApiService.fetchProducts(),
    ]);
    return { placeTypes, placeTargets, categories, products };
  },

  transformToFormData: (data, resource) => {
    let formData = new FormData();
    for (let key in data) {
      if (typeof data[key] === "undefined" || data[key] === null) continue;
      if (Array.isArray(data[key]) && data[key].some((i) => i?.rawFile)) {
        // Array of images
        if (resource === "products") {
          if (key === "images") {
            const arrUrls = data[key].filter((i) => !i.rawFile);
            formData.append(key, JSON.stringify(arrUrls));

            const arrFiles = data[key]
              .filter((i) => i.rawFile)
              .map((i) => i.rawFile);
            arrFiles.forEach((i) => {
              formData.append(UPLOADED_IMAGES_KEY, i);
            });
          }
        } else if (resource === "restaurants") {
          if (key === "coverImages" || key === "productImages") {
            const arrUrls = data[key]
              .filter((i) => !i.rawFile)
              .map((i) => i.url);
            formData.append(key, JSON.stringify(arrUrls));

            const arrFiles = data[key]
              .filter((i) => i.rawFile)
              .map((i) => i.rawFile);
            arrFiles.forEach((i) => {
              formData.append(
                key === "coverImages"
                  ? UPLOADED_RESTAURANT_COVER_IMAGES_KEY
                  : UPLOADED_RESTAURANT_PRODUCT_IMAGES_KEY,
                i
              );
            });
          }
        }
      } else if (data[key]?.rawFile) {
        if (resource === "videos" && key === "thumbnail") {
          formData.append(UPLOADED_VIDEOS_THUMBNAIL_KEY, data[key].rawFile);
        } else {
          formData.append(key, data[key].rawFile);
        }
      } else if (Array.isArray(data[key]) || typeof data[key] === "object") {
        if (key === "coverImages" || key === "productImages") {
          formData.append(key, JSON.stringify(data[key].map((i) => i.url)));
        } else {
          formData.append(key, JSON.stringify(data[key]));
        }
      } else {
        formData.append(key, data[key]);
      }
    }
    return formData;
  },

  updateRestaurant: async (restaurantId, data) => {
    const token = localStorage.getItem("token");
    const dataToSend = ApiService.transformToFormData(data, "restaurants");
    const res = await fetch(
      `${REACT_APP_BASE_URL}/restaurants/${restaurantId}`,
      {
        method: "PUT",
        body: dataToSend,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    validateAuthorization(res);
    if (!res.ok) {
      return { ok: false, ...(await res.json()) };
    }
    return { ok: true, data: await res.json() };
  },

  createOrUpdateProduct: async (product, restaurantId) => {
    const token = localStorage.getItem("token");
    try {
      const productToSend = { ...product };

      const formData = new FormData();

      Object.keys(productToSend).forEach((key) => {
        if (key !== "images") {
          formData.append(
            key,
            typeof productToSend[key] === "object"
              ? JSON.stringify(productToSend[key])
              : productToSend[key]
          );
        }
      });

      formData.append("restaurant", restaurantId);

      if (productToSend.images && productToSend.images.length > 0) {
        const urlImages = productToSend.images.filter((img) => !img.rawFile);
        if (urlImages.length > 0) {
          formData.append(
            "images",
            JSON.stringify(urlImages.map((e) => e.url))
          );
        }

        const fileImages = productToSend.images.filter((img) => img.rawFile);
        fileImages.forEach((img) => {
          formData.append(UPLOADED_IMAGES_KEY, img.rawFile);
        });
      }
      if (productToSend._id) {
        return fetch(`${REACT_APP_BASE_URL}/products/${productToSend._id}`, {
          method: "PUT",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      const result = await fetch(`${REACT_APP_BASE_URL}/products`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      validateAuthorization(result);
      if (!result.ok) {
        return null;
      }
      return result.json();
    } catch (error) {
      console.error("Error al crear el producto", error);
      return null;
    }
  },

  completeOnboarding: async (restaurantId, data) => {
    const token = localStorage.getItem("token");
    const result = await fetch(
      `${REACT_APP_BASE_URL}/restaurants/${restaurantId}/onboarding/complete`,
      {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    validateAuthorization(result);
    if (!result.ok) {
      throw new Error("Error al completar el onboarding");
    }
    return result.json();
  },
  changePassword: async (oldPassword, newPassword) => {
    const token = localStorage.getItem("token");
    try {
      const result = await fetch(`${REACT_APP_BASE_URL}/users/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword,
          newPassword,
        }),
      });
      validateAuthorization(result);
      return result.json();
    } catch (error) {
      console.error("Error al cambiar la contraseña", error);
      return null;
    }
  },
  checkRegion: async ([lng, lat]) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(
        `${REACT_APP_BASE_URL}/regions/location?location=${JSON.stringify([
          lng,
          lat,
        ])}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          method: "GET",
        }
      );
      validateAuthorization(res);
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error al verificar la región", error);
      return null;
    }
  },
  deleteProduct: async (productId) => {
    try {
      const token = localStorage.getItem("token");
      const result = await fetch(
        `${REACT_APP_BASE_URL}/products/${productId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      validateAuthorization(result);
      if (!result.ok) {
        return null;
      }
      const json = await result.json();
      return json;
    } catch (error) {
      console.error("Error al eliminar el producto", error);
      return null;
    }
  },
  // called when the user attempts to log in
  isUserConfirmed: () => {
    const token = localStorage.getItem("token");

    return fetch(`${REACT_APP_BASE_URL}/auth/is-confirmed`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        validateAuthorization(response);
        return response.json();
      })
      .catch((error) => {
        console.error("Error checking if user is confirmed:", error);
        throw new Error(
          `Error checking if user is confirmed: ${error.message}`
        );
      });
  },
};
